/** Curated IANA timezones for usage chart day boundaries. */
export type AgencyTimezoneOption = {
  value: string;
  label: string;
};

export const AGENCY_TIMEZONE_OPTIONS: AgencyTimezoneOption[] = [
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Toronto', label: 'Toronto (ET)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'Atlantic/Reykjavik', label: 'Reykjavik (GMT)' },
  { value: 'Europe/London', label: 'London (UK)' },
  { value: 'Europe/Dublin', label: 'Dublin (IST)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET)' },
  { value: 'Europe/Rome', label: 'Rome (CET)' },
  { value: 'Europe/Zurich', label: 'Zurich (CET)' },
  { value: 'Europe/Stockholm', label: 'Stockholm (CET)' },
  { value: 'Europe/Warsaw', label: 'Warsaw (CET)' },
  { value: 'Europe/Athens', label: 'Athens (EET)' },
  { value: 'Europe/Istanbul', label: 'Istanbul (TRT)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST)' },
  { value: 'UTC', label: 'UTC' }
];

/** US and Canada zones for usage day-boundary controls. */
export const US_CANADA_TIMEZONE_OPTIONS: AgencyTimezoneOption[] = [
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Vancouver', label: 'Pacific – Canada (PT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Edmonton', label: 'Mountain – Canada (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Winnipeg', label: 'Central – Canada (CT)' },
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Toronto', label: 'Eastern – Canada (ET)' },
  { value: 'America/Halifax', label: 'Atlantic (AT)' },
  { value: 'America/St_Johns', label: 'Newfoundland (NT)' }
];

/** Browser IANA timezone, or UTC when unavailable. */
export const browserTimezone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/** Prefer the browser zone when it is US/Canada; otherwise Eastern. */
export const defaultUsCanadaTimezone = (): string => {
  const browser = browserTimezone();
  if (US_CANADA_TIMEZONE_OPTIONS.some((option) => option.value === browser)) {
    return browser;
  }
  return 'America/New_York';
};

export const timezoneOptionsForValue = (value: string): AgencyTimezoneOption[] => {
  if (AGENCY_TIMEZONE_OPTIONS.some((option) => option.value === value)) {
    return AGENCY_TIMEZONE_OPTIONS;
  }
  return [{ value, label: value }, ...AGENCY_TIMEZONE_OPTIONS];
};

export const usCanadaTimezoneOptionsForValue = (value: string): AgencyTimezoneOption[] => {
  if (US_CANADA_TIMEZONE_OPTIONS.some((option) => option.value === value)) {
    return US_CANADA_TIMEZONE_OPTIONS;
  }
  return [{ value, label: value }, ...US_CANADA_TIMEZONE_OPTIONS];
};

/** Friendly label for an IANA timezone (falls back to the raw value). */
export const formatAgencyTimezoneLabel = (iana: string): string =>
  US_CANADA_TIMEZONE_OPTIONS.find((option) => option.value === iana)?.label ??
  AGENCY_TIMEZONE_OPTIONS.find((option) => option.value === iana)?.label ??
  iana;
